// src/api/api.ts with validation support

import { SimulationRequest, SimulationResult, RootingInterests, ChancesOverTimeResponse } from '../types/types';
import { auth } from '../config/firebase';
import { validateKnownWinnersFile, validateAndCleanKnownWinners } from '../utils/bracketValidator';
import { RoundConfig } from '../context/ConfigContext';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// --- Impersonation state ---
let _impersonateUid: string | null = null;
export const setImpersonateUid = (uid: string | null) => { _impersonateUid = uid; };
export const getImpersonateUid = (): string | null => _impersonateUid;

// Helper function to get auth token
const getAuthToken = async () => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('User not authenticated');
  }

  return user.getIdToken(true);
};

// Helper to build auth headers (includes impersonation header when active)
const getAuthHeaders = async (includeContentType = false): Promise<Record<string, string>> => {
  const token = await getAuthToken();
  const headers: Record<string, string> = {
    'Authorization': `Bearer ${token}`
  };
  if (includeContentType) {
    headers['Content-Type'] = 'application/json';
  }
  if (_impersonateUid) {
    headers['X-Impersonate-UID'] = _impersonateUid;
  }
  return headers;
};

// Helper function for handling auth errors
const handleAuthError = (error: any) => {
  if (error.message === 'User not authenticated') {
    // Redirect to login page
    window.location.href = '/login';
  }
  throw error;
};

// Internal fetch with AbortController timeout
const _fetchWithTimeout = async (url: string, options: RequestInit = {}, timeoutMs = 30000): Promise<Response> => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const response = await fetch(url, { ...options, signal: controller.signal });
    return response;
  } finally {
    clearTimeout(timeoutId);
  }
};

// Fetch with timeout + automatic 401 retry (force-refreshes auth token and retries once)
const fetchWithTimeout = async (url: string, options: RequestInit = {}, timeoutMs = 30000): Promise<Response> => {
  const response = await _fetchWithTimeout(url, options, timeoutMs);
  if (response.status === 401) {
    const user = auth.currentUser;
    if (!user) {
      window.location.href = '/login';
      throw new Error('User not authenticated');
    }
    // Token may be stale — force refresh and retry once
    const freshToken = await user.getIdToken(true);
    const headers = { ...(options.headers as Record<string, string>), 'Authorization': `Bearer ${freshToken}` };
    const retryResponse = await _fetchWithTimeout(url, { ...options, headers }, timeoutMs);
    if (retryResponse.status === 401) {
      window.location.href = '/login';
      throw new Error('Session expired. Please log in again.');
    }
    return retryResponse;
  }
  return response;
};


// Tournament configuration API functions
export const saveTournamentConfig = async (roundConfigs: RoundConfig[]): Promise<void> => {
  try {
    const response = await fetchWithTimeout(`${API_BASE_URL}/save-tournament-config`, {
      method: 'POST',
      headers: await getAuthHeaders(true),
      body: JSON.stringify({
        roundConfigs // Only round names now, no scores
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to save tournament config: ${errorText}`);
    }
  } catch (error) {
    console.error('Error saving tournament config:', error);
    handleAuthError(error);
    throw error;
  }
};


export const getTournamentConfig = async (): Promise<RoundConfig[]> => {
  try {
    // Check if user is authenticated before making API call
    const user = auth.currentUser;
    if (!user) {
      return getDefaultConfig();
    }

    const response = await fetchWithTimeout(`${API_BASE_URL}/get-tournament-config`, {
      headers: await getAuthHeaders()
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to get tournament config: ${errorText}`);
    }

    const data = await response.json();
    return data.roundConfigs || [];
  } catch (error) {
    console.error('Error getting tournament config:', error);
    return getDefaultConfig();
  }
};

// Helper function for default config
const getDefaultConfig = (): RoundConfig[] => [
  { name: "Round of 64" },
  { name: "Round of 32" },
  { name: "Round of 16" },
  { name: "Round of 8" },
  { name: "Round of 4" },
  { name: "Championship Game" },
];

// Delete a saved scenario
export const deleteScenario = async (scenarioName: string): Promise<void> => {
  try {
    const response = await fetchWithTimeout(`${API_BASE_URL}/delete-scenario`, {
      method: 'DELETE',
      headers: await getAuthHeaders(true),
      body: JSON.stringify({
        name: scenarioName
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to delete scenario: ${errorText}`);
    }
  } catch (error) {
    console.error('Error deleting scenario:', error);
    handleAuthError(error);
    throw error;
  }
};

// Run the tournament simulation
export const simulateTournament = async (
  request: SimulationRequest & {
    roundNames?: string[];
    pointsPerRound?: number[];
  }
): Promise<SimulationResult> => {
  try {
    // Validate the known winners before sending
    const { knownWinners } = request;
    if (knownWinners && knownWinners.length > 0) {
      // Clean the winners array to ensure bracket consistency
      const { cleanedWinners, isValid, errorMessage } = validateAndCleanKnownWinners(knownWinners);
      
      // If validation failed, use the cleaned winners instead
      if (!isValid) {
        console.warn(`Bracket inconsistencies found: ${errorMessage}. Using cleaned winners array.`);
        request.knownWinners = cleanedWinners;
      }
    }
    
    // If poolId is set, omit playerPicks to avoid sending large payload
    const { playerPicks: _picks, ...rest } = request;
    const body = request.poolId ? rest : request;

    const response = await fetchWithTimeout(`${API_BASE_URL}/simulate`, {
      method: 'POST',
      headers: await getAuthHeaders(true),
      body: JSON.stringify(body),
    }, 150000);

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      if (response.status === 403 && errorData?.error === 'pro_required') {
        const err = new Error('Pro subscription required');
        (err as any).code = 'pro_required';
        throw err;
      }
      const message = response.status === 429 ? 'Too many requests' : (errorData?.error || response.statusText);
      throw new Error(`Simulation failed: ${message}`);
    }

    // Parse response as JSON
    const responseData = await response.json();

    // Ensure the response has the expected structure
    const result: SimulationResult = {
      // Ensure players is an array
      players: Array.isArray(responseData.players) ? responseData.players : [],
      totalScenarios: responseData.totalScenarios || 0,
      totalPossibleScenarios: responseData.totalPossibleScenarios || 0,
      tiesPossible: responseData.tiesPossible || 0,
      tiesPercentage: responseData.tiesPercentage || 0,
      sampled: Boolean(responseData.sampled)
    };
    
    // Check if players array is empty but we have data
    if (result.players.length === 0 && responseData.players) {
      // If players is an object with player names as keys
      if (typeof responseData.players === 'object' && !Array.isArray(responseData.players)) {
        result.players = Object.entries(responseData.players).map(([name, data]: [string, any]) => ({
          name,
          wins: data.wins || 0,
          percentage: data.percentage || 0
        }));
      }
    }
    
    return result;
  } catch (error) {
    console.error('Error simulating tournament:', error);
    handleAuthError(error);
    throw error;
  }
};

// Get chances over time (progressive simulation snapshots)
export const getChancesOverTime = async (
  playerPicks: string,
  roundNames?: string[],
  pointsPerRound?: number[],
  poolId?: string
): Promise<ChancesOverTimeResponse> => {
  try {
    const payload: Record<string, any> = { roundNames, pointsPerRound };
    if (poolId) {
      payload.poolId = poolId;
    } else {
      payload.playerPicks = playerPicks;
    }

    // 30-minute timeout — first run can be very slow
    const response = await fetchWithTimeout(`${API_BASE_URL}/chances-over-time`, {
      method: 'POST',
      headers: await getAuthHeaders(true),
      body: JSON.stringify(payload),
    }, 1800000);

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      if (response.status === 403 && errorData?.error === 'pro_required') {
        const err = new Error('Pro subscription required');
        (err as any).code = 'pro_required';
        throw err;
      }
      const message = response.status === 429
        ? 'Too many requests'
        : (errorData?.error || response.statusText);
      throw new Error(`Chances over time failed: ${message}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching chances over time:', error);
    handleAuthError(error);
    throw error;
  }
};

// Get rooting interests for a specific player
export const getPlayerRootingInterests = async (
  playerName: string,
  playerPicks: string,
  knownWinners: string[],
  roundNames?: string[],
  pointsPerRound?: number[],
  poolId?: string
): Promise<RootingInterests> => {
  try {
    // Validate and clean known winners before sending
    if (knownWinners && knownWinners.length > 0) {
      const { cleanedWinners, isValid } = validateAndCleanKnownWinners(knownWinners);
      if (!isValid) {
        console.warn('Bracket inconsistencies found in rooting interests request. Using cleaned winners.');
        knownWinners = cleanedWinners;
      }
    }

    // If poolId is set, omit playerPicks to avoid sending large payload
    const payload: Record<string, any> = {
      playerName,
      knownWinners,
      sampleSize: 10000,
      roundNames,
      pointsPerRound
    };
    if (poolId) {
      payload.poolId = poolId;
    } else {
      payload.playerPicks = playerPicks;
    }

    const response = await fetchWithTimeout(`${API_BASE_URL}/player-interests`, {
      method: 'POST',
      headers: await getAuthHeaders(true),
      body: JSON.stringify(payload),
    }, 150000);

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      if (response.status === 403 && errorData?.error === 'pro_required') {
        const err = new Error('Pro subscription required');
        (err as any).code = 'pro_required';
        throw err;
      }
      const message = response.status === 429 ? 'Too many requests' : (errorData?.error || response.statusText);
      throw new Error(`Failed to get rooting interests: ${message}`);
    }

    return await response.json() as RootingInterests;
  } catch (error) {
    console.error('Error getting player rooting interests:', error);
    handleAuthError(error);
    throw error;
  }
};

// Get known winners from the backend with validation
export const getKnownWinners = async (): Promise<string[]> => {
  try {
    const response = await fetchWithTimeout(`${API_BASE_URL}/known-winners`, {
      headers: await getAuthHeaders()
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      console.error('Error fetching known winners:', errorData);
      
      // If there are invalid teams, log them specifically
      if (errorData.invalid_teams && errorData.invalid_teams.length > 0) {
        console.error('Invalid team names in known_winners.txt:');
        errorData.invalid_teams.forEach((team: {index: number, name: string}) => {
          console.error(`Game ${team.index}: "${team.name}"`);
        });
      }
      
      throw new Error(errorData.error || 'Failed to fetch known winners');
    }
    
    const data = await response.json();
    const winners = data.winners || [];
    
    // Validate winners for bracket consistency
    const validation = validateKnownWinnersFile(winners.join(','));
    if (!validation.isValid) {
      console.error('Validation failed for known_winners.txt:', validation.errorMessage);
      // Return empty array on validation failure - error will be displayed by the UI
      return [];
    }
    
    return winners;
  } catch (error) {
    console.error('Error fetching known winners:', error);
    handleAuthError(error);
    return [];
  }
};

// Save a bracket with validation
export const saveBracket = async (name: string, knownWinners: string[]): Promise<void> => {
  try {
    // Validate the bracket structure before saving
    const { cleanedWinners, isValid, errorMessage } = validateAndCleanKnownWinners(knownWinners);
    if (!isValid) {
      console.warn(`Bracket inconsistencies found when saving: ${errorMessage}. Using cleaned winners.`);
      knownWinners = cleanedWinners;
    }
    
    const response = await fetchWithTimeout(`${API_BASE_URL}/save-bracket`, {
      method: 'POST',
      headers: await getAuthHeaders(true),
      body: JSON.stringify({
        name,
        knownWinners
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to save bracket: ${errorText}`);
    }
  } catch (error) {
    console.error('Error saving bracket:', error);
    handleAuthError(error);
    throw error;
  }
};

// Get user's saved brackets
export const getUserBrackets = async (): Promise<Array<{name: string, knownWinners: string[]}>> => {
  try {
    const response = await fetchWithTimeout(`${API_BASE_URL}/user-brackets`, {
      headers: await getAuthHeaders()
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to get brackets: ${errorText}`);
    }

    const data = await response.json();
    const brackets = data.brackets || [];
    
    // Validate each bracket's structure
    return brackets.map((bracket: {name: string, knownWinners: string[], created_at?: string}) => {
      const { cleanedWinners, isValid } = validateAndCleanKnownWinners(bracket.knownWinners);
      if (!isValid) {
        console.warn(`Loaded bracket "${bracket.name}" has inconsistencies. Using cleaned version.`);
        return { ...bracket, knownWinners: cleanedWinners };
      }
      return bracket;
    });
  } catch (error) {
    console.error('Error getting user brackets:', error);
    handleAuthError(error);
    throw error;
  }
};

// === NEW POOL MANAGEMENT FUNCTIONS ===

// Save a player picks pool
export const savePool = async (name: string, playerPicks: string): Promise<void> => {
  try {
    const response = await fetchWithTimeout(`${API_BASE_URL}/save-pool`, {
      method: 'POST',
      headers: await getAuthHeaders(true),
      body: JSON.stringify({
        name,
        playerPicks
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to save pool: ${errorText}`);
    }
  } catch (error) {
    console.error('Error saving pool:', error);
    handleAuthError(error);
    throw error;
  }
};

export interface PoolWithConfig {
  name: string;
  playerPicks: string;
  created_at: string;
  pointsConfig?: {
    pointValues: number[]; // Only point values, no round names
  };
  pinnedPlayers?: string[];
}

export const savePoolWithConfig = async (
  name: string,
  playerPicks: string,
  pointsConfig: {pointValues: number[]} // Only point values now
): Promise<void> => {
  try {
    const response = await fetchWithTimeout(`${API_BASE_URL}/save-pool`, {
      method: 'POST',
      headers: await getAuthHeaders(true),
      body: JSON.stringify({
        name,
        playerPicks,
        pointsConfig // Include point configuration in the request
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to save pool: ${errorText}`);
    }
  } catch (error) {
    console.error('Error saving pool with config:', error);
    handleAuthError(error);
    throw error;
  }
};

export const getUserPoolsWithConfig = async (): Promise<PoolWithConfig[]> => {
  try {
    const response = await fetchWithTimeout(`${API_BASE_URL}/user-pools`, {
      headers: await getAuthHeaders()
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to get pools: ${errorText}`);
    }

    const data = await response.json();
    
    // Ensure each pool has proper structure with default configuration if missing
    return (data.pools || []).map((pool: any) => ({
      name: pool.name,
      playerPicks: pool.playerPicks,
      created_at: pool.created_at,
      pointsConfig: pool.pointsConfig || {
        pointValues: [1, 1, 2, 4, 8, 16, 32] // Default 7 rounds
      },
      pinnedPlayers: pool.pinnedPlayers || []
    }));
  } catch (error) {
    console.error('Error getting user pools with config:', error);
    handleAuthError(error);
    throw error;
  }
};

// Get user's saved pools
export const getUserPools = async (): Promise<Array<{name: string, playerPicks: string, created_at: string}>> => {
  try {
    const response = await fetchWithTimeout(`${API_BASE_URL}/user-pools`, {
      headers: await getAuthHeaders()
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to get pools: ${errorText}`);
    }

    const data = await response.json();
    return data.pools || [];
  } catch (error) {
    console.error('Error getting user pools:', error);
    handleAuthError(error);
    throw error;
  }
};

// Delete a pool
export const deletePool = async (poolName: string): Promise<void> => {
  try {
    const response = await fetchWithTimeout(`${API_BASE_URL}/delete-pool`, {
      method: 'DELETE',
      headers: await getAuthHeaders(true),
      body: JSON.stringify({
        name: poolName
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to delete pool: ${errorText}`);
    }
  } catch (error) {
    console.error('Error deleting pool:', error);
    handleAuthError(error);
    throw error;
  }
};

// === POOL IMPORT FUNCTIONS ===

export interface PoolImportResult {
  success: boolean;
  pool_id: string;
  entries_imported: number;
  entries_skipped: number;
  skip_reasons: string[];
  truncated?: boolean;
  total_entries?: number;
}

export const importEspnPool = async (groupId: string): Promise<PoolImportResult> => {
  try {
    const response = await fetchWithTimeout(`${API_BASE_URL}/create-espn-pool`, {
      method: 'POST',
      headers: await getAuthHeaders(true),
      body: JSON.stringify({ group_id: groupId }),
    }, 600000);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Import failed' }));
      throw new Error(errorData.error || `Import failed with status ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error importing ESPN pool:', error);
    handleAuthError(error);
    throw error;
  }
};

export const importCbsPool = async (cbsPoolId: string): Promise<PoolImportResult> => {
  try {
    const response = await fetchWithTimeout(`${API_BASE_URL}/create-cbs-pool`, {
      method: 'POST',
      headers: await getAuthHeaders(true),
      body: JSON.stringify({ cbs_pool_id: cbsPoolId }),
    }, 600000);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Import failed' }));
      throw new Error(errorData.error || `Import failed with status ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error importing CBS pool:', error);
    handleAuthError(error);
    throw error;
  }
};

export const importYahooPool = async (groupId: string): Promise<PoolImportResult> => {
  try {
    const response = await fetchWithTimeout(`${API_BASE_URL}/create-yahoo-pool`, {
      method: 'POST',
      headers: await getAuthHeaders(true),
      body: JSON.stringify({ group_id: groupId }),
    }, 600000);

    if (!response.ok) {
      if (response.status === 403) {
        throw new Error('Cannot import Yahoo private pool. Yahoo pools must be public for import.');
      }
      const errorData = await response.json().catch(() => ({ error: 'Import failed' }));
      throw new Error(errorData.error || `Import failed with status ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error importing Yahoo pool:', error);
    handleAuthError(error);
    throw error;
  }
};

// Send one-time welcome email (fire-and-forget, never throws)
// Optional email param for providers (e.g. Twitter) where the token may not yet contain the email
export const sendWelcomeEmail = async (email?: string): Promise<{ sent: boolean }> => {
  try {
    const response = await fetchWithTimeout(`${API_BASE_URL}/send-welcome-email`, {
      method: 'POST',
      headers: await getAuthHeaders(!!email),
      ...(email ? { body: JSON.stringify({ email }) } : {}),
    });
    if (!response.ok) {
      return { sent: false };
    }
    return await response.json();
  } catch {
    return { sent: false };
  }
};

// Save Twitter/X handle (fire-and-forget, never throws)
export const saveTwitterHandle = async (handle: string): Promise<void> => {
  try {
    await fetchWithTimeout(`${API_BASE_URL}/save-twitter-handle`, {
      method: 'POST',
      headers: await getAuthHeaders(true),
      body: JSON.stringify({ handle }),
    });
  } catch {
    // best-effort, don't block sign-in
  }
};

// Fetch user pro status
export const fetchUserStatus = async (): Promise<{ isPro: boolean; isSuperuser?: boolean; email?: string; twitterHandle?: string }> => {
  const response = await fetchWithTimeout(`${API_BASE_URL}/user-status`, {
    headers: await getAuthHeaders()
  });
  if (response.status === 404) {
    throw new Error('User not found');
  }
  if (!response.ok) {
    throw new Error('Failed to fetch user status');
  }
  return await response.json();
};

// Create a Stripe checkout session for Pro upgrade
export const createCheckoutSession = async (): Promise<{ url: string }> => {
  const response = await fetchWithTimeout(`${API_BASE_URL}/create-checkout-session`, {
    method: 'POST',
    headers: await getAuthHeaders(true)
  });
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to create checkout session: ${errorText}`);
  }
  return await response.json();
};

// Update pinned players for a pool
export const updatePoolPinnedPlayers = async (
  poolName: string,
  pinnedPlayers: string[]
): Promise<void> => {
  try {
    const response = await fetchWithTimeout(`${API_BASE_URL}/pool-pinned-players`, {
      method: 'PUT',
      headers: await getAuthHeaders(true),
      body: JSON.stringify({ poolName, pinnedPlayers })
    });
    if (!response.ok) {
      throw new Error(`Failed to update pinned players: ${await response.text()}`);
    }
  } catch (error) {
    console.error('Error updating pinned players:', error);
    handleAuthError(error);
    throw error;
  }
};

// Admin: Grant pro status to a user (superuser only)
export const grantPro = async (uid: string): Promise<{ success: boolean; uid: string }> => {
  const response = await fetchWithTimeout(`${API_BASE_URL}/admin/grant-pro`, {
    method: 'POST',
    headers: await getAuthHeaders(true),
    body: JSON.stringify({ uid })
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: 'Grant pro failed' }));
    throw new Error(errorData.error || 'Grant pro failed');
  }
  return await response.json();
};

// Admin: Save known winners to backend file (superuser only)
export const saveToKnownWinners = async (knownWinners: string[]): Promise<void> => {
  const response = await fetchWithTimeout(`${API_BASE_URL}/admin/save-known-winners`, {
    method: 'POST',
    headers: await getAuthHeaders(true),
    body: JSON.stringify({ knownWinners }),
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: 'Save failed' }));
    throw new Error(errorData.error || 'Save failed');
  }
};

export const getAdminStats = async () => {
  const response = await fetchWithTimeout(`${API_BASE_URL}/admin/stats`, {
    headers: await getAuthHeaders()
  });
  if (!response.ok) throw new Error('Failed to fetch stats');
  return await response.json();
};

// --- Share scenario API functions ---

export const createShare = async (data: {
  scenarioName: string;
  poolName: string;
  knownWinners: string[];
  playerPicks: string;
  pointValues: number[];
}): Promise<{ success: boolean; shareCode: string; shareUrl: string }> => {
  const response = await fetchWithTimeout(`${API_BASE_URL}/create-share`, {
    method: 'POST',
    headers: await getAuthHeaders(true),
    body: JSON.stringify(data),
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: 'Failed to create share' }));
    throw new Error(errorData.error || 'Failed to create share');
  }
  return await response.json();
};

export const getSharedScenario = async (shareCode: string): Promise<import('../types/types').SharedScenarioData> => {
  // Public endpoint — no auth header
  const response = await _fetchWithTimeout(`${API_BASE_URL}/shared/${encodeURIComponent(shareCode)}`);
  if (!response.ok) {
    if (response.status === 404) {
      throw new Error('Shared scenario not found');
    }
    throw new Error('Failed to fetch shared scenario');
  }
  return await response.json();
};

export const claimSharedPool = async (shareCode: string): Promise<{ success: boolean; poolName: string }> => {
  const response = await fetchWithTimeout(`${API_BASE_URL}/claim-shared-pool`, {
    method: 'POST',
    headers: await getAuthHeaders(true),
    body: JSON.stringify({ shareCode }),
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: 'Failed to claim pool' }));
    throw new Error(errorData.error || 'Failed to claim pool');
  }
  return await response.json();
};